/** Schreibwege der Finanzen (B-01, BE-05): Buchungen, Budgets (Kategorien) und Abos. Betraege in Cent. */
import { db } from '@/db/schema';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { euro, faelligeTermine } from '@/domain/finanzen/finanzen';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { Account, Envelope, ID, LocalDate, Subscription, Transaction } from '@/domain/types';

/** Das Konto, auf das gebucht wird. Ole fuehrt zunaechst nur eines - es entsteht beim ersten Buchen. */
export async function standardKonto(): Promise<Account> {
  // Das aelteste Konto ist das Standardkonto (die Reihenfolge nach `id` waere zufaellig).
  const vorhanden = (await db.accounts.toArray()).sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
  if (vorhanden) return vorhanden;
  const ts = nowInstant();
  const konto: Account = { id: newId(), name: 'Konto', kind: 'giro', openingBalanceCents: 0, areaId: null, createdAt: ts, updatedAt: ts };
  await db.accounts.add(konto);
  return konto;
}

export async function startsaldoSetzen(cent: number): Promise<void> {
  const k = await standardKonto();
  await db.accounts.update(k.id, { openingBalanceCents: cent, updatedAt: nowInstant() });
}

export interface BuchungsEntwurf {
  date: LocalDate;
  /** Positiver Betrag in Cent; das Vorzeichen ergibt sich aus der Art. */
  cent: number;
  kind: 'ausgabe' | 'einnahme';
  envelopeId?: ID;
  payee?: string;
  note?: string;
  /** Ohne Angabe das erste Konto. */
  accountId?: ID;
  subscriptionId?: ID;
}

export async function buchungAnlegen(e: BuchungsEntwurf, areaId: ID | null = null): Promise<Transaction> {
  if (!Number.isInteger(e.cent) || e.cent <= 0) throw new Error('Der Betrag muss größer als 0 sein.');
  const ts = nowInstant();
  const kontoId = e.accountId ?? (await standardKonto()).id;
  const t: Transaction = {
    id: newId(), date: e.date, amountCents: e.kind === 'ausgabe' ? -e.cent : e.cent, accountId: kontoId, kind: e.kind,
    ...(e.subscriptionId ? { subscriptionId: e.subscriptionId } : {}),
    ...(e.kind === 'ausgabe' && e.envelopeId ? { envelopeId: e.envelopeId } : {}),
    ...(e.payee?.trim() ? { payee: e.payee.trim() } : {}),
    ...(e.note?.trim() ? { note: e.note.trim() } : {}),
    areaId, createdAt: ts, updatedAt: ts,
  };
  await db.transactions.add(t);
  return t;
}

export type BuchungsAenderung = Partial<Pick<BuchungsEntwurf, 'date' | 'cent' | 'kind' | 'envelopeId' | 'payee' | 'accountId'>>;

/** Ändert eine Buchung; Betrag und Art ergeben wie beim Anlegen das Vorzeichen. Umbuchungen bleiben unberührt. */
export async function buchungAendern(id: ID, patch: BuchungsAenderung): Promise<void> {
  const t = await db.transactions.get(id);
  if (!t || t.kind === 'umbuchung') return;
  const kind = patch.kind ?? (t.kind as 'ausgabe' | 'einnahme');
  const cent = patch.cent ?? Math.abs(t.amountCents);
  if (!Number.isInteger(cent) || cent <= 0) throw new Error('Der Betrag muss größer als 0 sein.');
  const envelopeId = kind === 'ausgabe' ? ('envelopeId' in patch ? patch.envelopeId : t.envelopeId) : undefined;
  const neu: Transaction = { ...t, date: patch.date ?? t.date, kind, amountCents: kind === 'ausgabe' ? -cent : cent, accountId: patch.accountId ?? t.accountId, updatedAt: nowInstant() };
  if (envelopeId) neu.envelopeId = envelopeId; else delete neu.envelopeId;
  if ('payee' in patch) { if (patch.payee?.trim()) neu.payee = patch.payee.trim(); else delete neu.payee; }
  await db.transactions.put(neu);
}

export async function kontoAnlegen(name: string, kind: Account['kind'], startsaldoCent: number): Promise<Account> {
  const ts = nowInstant();
  const k: Account = { id: newId(), name: name.trim() || 'Konto', kind, openingBalanceCents: startsaldoCent, areaId: null, createdAt: ts, updatedAt: ts };
  await db.accounts.add(k);
  return k;
}

/** Umbuchung zwischen zwei Konten: zwei Zeilen (minus und plus), die in Einnahmen und Ausgaben nicht zaehlen. */
export async function umbuchen(vonId: ID, nachId: ID, cent: number, date: LocalDate, note?: string): Promise<void> {
  if (vonId === nachId) throw new Error('Von- und Nach-Konto müssen verschieden sein.');
  if (!Number.isInteger(cent) || cent <= 0) throw new Error('Der Betrag muss größer als 0 sein.');
  const ts = nowInstant();
  const zeile = (accountId: ID, amountCents: number): Transaction => ({ id: newId(), date, amountCents, accountId, kind: 'umbuchung', ...(note?.trim() ? { note: note.trim() } : {}), areaId: null, createdAt: ts, updatedAt: ts });
  await db.transactions.bulkAdd([zeile(vonId, -cent), zeile(nachId, cent)]);
}

/**
 * B-02: Tragt faellige Zahlungen der Abos mit `autoBuchen` als Buchung ein und
 * setzt `nextDue` weiter. Verpasste Termine werden nachgeholt; eine schon
 * vorhandene Buchung zum selben Abo und Tag wird nicht doppelt angelegt.
 * Gibt die Zahl neuer Buchungen zurueck.
 */
let abrechnung: Promise<unknown> = Promise.resolve();

/** Laeuft nacheinander, auch wenn mehrere Kacheln es gleichzeitig anstossen - sonst koennten zwei Laeufe doppelt buchen. */
export function abosAbrechnen(heute: LocalDate): Promise<number> {
  const lauf = abrechnung.then(() => abosAbrechnenEinmal(heute));
  abrechnung = lauf.catch(() => undefined);
  return lauf;
}

async function abosAbrechnenEinmal(heute: LocalDate): Promise<number> {
  const abos = (await db.subscriptions.toArray()).filter((s) => s.autoBuchen && s.nextDue <= heute);
  let neu = 0;
  for (const abo of abos) {
    const { termine, naechste } = faelligeTermine(abo, heute);
    await db.transaction('rw', db.transactions, db.subscriptions, db.accounts, async () => {
      const vorhanden = new Set((await db.transactions.where('date').anyOf(termine).filter((t) => t.subscriptionId === abo.id).toArray()).map((t) => t.date));
      for (const datum of termine) {
        if (vorhanden.has(datum)) continue;
        await buchungAnlegen({ date: datum, cent: abo.amountCents, kind: abo.kind ?? 'ausgabe', payee: abo.name, subscriptionId: abo.id, ...(abo.envelopeId ? { envelopeId: abo.envelopeId } : {}) });
        neu += 1;
      }
      await db.subscriptions.update(abo.id, { nextDue: naechste, updatedAt: nowInstant() });
    });
  }
  return neu;
}
export const buchungLoeschen = async (t: Transaction): Promise<void> => {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'transactions', zeileIds: [t.id], titel: `${t.payee ?? (t.kind === 'einnahme' ? 'Einnahme' : 'Ausgabe')} ${euro(Math.abs(t.amountCents))}` }, () => db.transactions.delete(t.id));
};

export async function kategorieAnlegen(name: string, budgetCent: number): Promise<Envelope> {
  const ts = nowInstant();
  const e: Envelope = { id: newId(), name: name.trim(), monthlyBudgetCents: Math.max(0, budgetCent), rollover: false, areaId: null, createdAt: ts, updatedAt: ts };
  await db.envelopes.add(e);
  return e;
}

export const kategorieAendern = (id: ID, patch: Partial<Pick<Envelope, 'name' | 'monthlyBudgetCents'>>): Promise<number> =>
  db.envelopes.update(id, { ...patch, updatedAt: nowInstant() });

/** Loescht die Kategorie; Buchungen bleiben und stehen danach "ohne Kategorie". */
export async function kategorieLoeschen(e: Envelope): Promise<void> {
  const ids = await db.transactions.where('envelopeId').equals(e.id).primaryKeys();
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'envelopes', zeileIds: [e.id], titel: e.name }, () => db.transaction('rw', db.envelopes, db.transactions, async () => {
    const ts = nowInstant();
    for (const id of ids) await db.transactions.update(id, { envelopeId: undefined, updatedAt: ts });
    await db.envelopes.delete(e.id);
  }));
}

export type AboEntwurf = Pick<Subscription, 'name' | 'amountCents' | 'cycle' | 'nextDue'> & Partial<Pick<Subscription, 'cancelBy' | 'noticePeriodDays' | 'autoBuchen' | 'kind' | 'envelopeId'>>;

export async function aboAnlegen(e: AboEntwurf, areaId: ID | null = null): Promise<Subscription> {
  const ts = nowInstant();
  const s: Subscription = {
    id: newId(), name: e.name.trim(), amountCents: e.amountCents, cycle: e.cycle, nextDue: e.nextDue,
    ...(e.cancelBy ? { cancelBy: e.cancelBy } : {}),
    ...(e.noticePeriodDays !== undefined ? { noticePeriodDays: e.noticePeriodDays } : {}),
    ...(e.autoBuchen ? { autoBuchen: true } : {}),
    ...(e.kind === 'einnahme' ? { kind: 'einnahme' as const } : {}),
    ...(e.envelopeId ? { envelopeId: e.envelopeId } : {}),
    areaId, createdAt: ts, updatedAt: ts,
  };
  await db.subscriptions.add(s);
  return s;
}

export const aboAendern = (id: ID, patch: Partial<AboEntwurf>): Promise<number> =>
  db.subscriptions.update(id, { ...patch, updatedAt: nowInstant() });

export const aboLoeschen = async (s: Subscription): Promise<void> => {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'subscriptions', zeileIds: [s.id], titel: s.name }, () => db.subscriptions.delete(s.id));
};

// --- Sparziele (Zeilen `sparziel:<id>` in `oberflaeche`, siehe domain/finanzen/sparziele.ts) ---

import { SPARZIEL_PRAEFIX, sparzielAus, type Sparziel } from '@/domain/finanzen/sparziele';

export async function sparzielAnlegen(areaId: ID, name: string, zielCent: number, monatlichCent?: number): Promise<Sparziel> {
  if (!Number.isInteger(zielCent) || zielCent <= 0) throw new Error('Das Ziel muss größer als 0 sein.');
  const s: Sparziel = {
    id: `${SPARZIEL_PRAEFIX}${newId()}`, areaId, name: name.trim() || 'Sparziel', zielCent, gespartCent: 0,
    ...(monatlichCent && monatlichCent > 0 ? { monatlichCent } : {}), updatedAt: nowInstant(),
  };
  await db.oberflaeche.add(s);
  return s;
}

/** Legt Geld zurueck (positiv) oder nimmt es heraus (negativ); der Stand bleibt zwischen 0 und dem Ziel plus Ueberschuss >= 0. */
export async function sparzielEinzahlen(id: string, cent: number): Promise<void> {
  const s = sparzielAus(await db.oberflaeche.get(id));
  if (!s) return;
  const neu: Sparziel = { ...s, gespartCent: Math.max(0, s.gespartCent + cent), updatedAt: nowInstant() };
  await db.oberflaeche.put(neu);
}

export async function sparzielRateSetzen(id: string, monatlichCent: number | undefined): Promise<void> {
  const s = sparzielAus(await db.oberflaeche.get(id));
  if (!s) return;
  const { monatlichCent: _alt, ...rest } = s;
  const neu: Sparziel = { ...rest, ...(monatlichCent && monatlichCent > 0 ? { monatlichCent } : {}), updatedAt: nowInstant() };
  await db.oberflaeche.put(neu);
}

export async function sparzielLoeschen(s: Sparziel): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'oberflaeche', zeileIds: [s.id], titel: `Sparziel ${s.name}` }, () => db.oberflaeche.delete(s.id));
}
